import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type {
  CreateOrderBoardStageInput,
  OrderBoardStage,
  UpdateOrderBoardStageInput,
} from "@school-sales-ai/shared";
import { api } from "@/lib/api";
import { useScopedClientId } from "@/hooks/useScopedClientId";

/**
 * The client's custom /pedidos columns — sub-stages of "Por entregar". The five
 * status columns are not here: they are code (`buildOrderColumns`).
 *
 * Scoped on `useScopedClientId` like `useOrdersTable`, so a super-admin switching
 * tenants never sees the previous tenant's columns. `enabled` is the caller's
 * plan check — the endpoint 403s a client without `orders`.
 */
export function useOrderBoardStages({ enabled = true }: { enabled?: boolean } = {}) {
  const clientId = useScopedClientId();
  return useQuery({
    queryKey: ["order-board-stages", clientId],
    queryFn: () => api.get<OrderBoardStage[]>("/crm/order-board-stages"),
    enabled: enabled && !!clientId,
  });
}

// A column change changes what the board renders, so every mutation refetches
// the columns and every order view.
function useInvalidateStages(): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ["order-board-stages"] });
    void queryClient.invalidateQueries({ queryKey: ["orders"] });
  };
}

export function useCreateOrderBoardStage() {
  const invalidate = useInvalidateStages();
  return useMutation({
    mutationFn: (input: CreateOrderBoardStageInput) =>
      api.post<OrderBoardStage>("/crm/order-board-stages", input),
    onSuccess: () => {
      toast.success("Columna agregada.");
      invalidate();
    },
    onError: (error: Error) => toast.error(error.message || "Error al agregar la columna."),
  });
}

export function useUpdateOrderBoardStage() {
  const invalidate = useInvalidateStages();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateOrderBoardStageInput }) =>
      api.patch<OrderBoardStage>(`/crm/order-board-stages/${id}`, data),
    onSuccess: () => {
      toast.success("Columna actualizada.");
      invalidate();
    },
    onError: (error: Error) => toast.error(error.message || "Error al actualizar la columna."),
  });
}

export function useDeleteOrderBoardStage() {
  const invalidate = useInvalidateStages();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/crm/order-board-stages/${id}`),
    onSuccess: () => {
      toast.success("Columna eliminada.");
      invalidate();
    },
    onError: (error: Error) => toast.error(error.message || "Error al eliminar la columna."),
  });
}

export function useReorderOrderBoardStages() {
  const invalidate = useInvalidateStages();
  return useMutation({
    mutationFn: (orderedIds: string[]) =>
      api.patch<OrderBoardStage[]>("/crm/order-board-stages/reorder", { orderedIds }),
    onSuccess: () => invalidate(),
    onError: (error: Error) => toast.error(error.message || "Error al reordenar las columnas."),
  });
}
